import type { ToolCallData } from '../../standalone/types';
import { cn } from '../../utils';
import { useT } from '../../i18n';
import styles from './ThinkingIndicator.module.css';

export interface ThinkingIndicatorProps {
  currentToolCall: ToolCallData | null;
  className?: string;
  /** Idle label shown beside the dots while no tool is executing (defaults to the localized "Thinking") */
  thinkingText?: string;
}

/** Spinning ring with the thinking label, or a tool-specific message while a tool runs. */
export function ThinkingIndicator({ currentToolCall, className, thinkingText }: ThinkingIndicatorProps) {
  const t = useT();
  let label = thinkingText || t('thinking.default');

  if (currentToolCall) {
    label = currentToolCall.executionMessage
      ? currentToolCall.executionMessage
      : t('thinking.executingTool', { tool: currentToolCall.displayName });
  }

  return (
    <div data-thinking-indicator="true" className={cn(styles.container, className)}>
      <div className={styles.pill}>
        <svg
          className={styles.spinner}
          xmlns="http://www.w3.org/2000/svg"
          fill="none"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <circle opacity={0.25} cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path
            opacity={0.75}
            fill="currentColor"
            d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
          />
        </svg>
        <span className={styles.text}>
          {label}
          <span className={styles.dots} aria-hidden="true" />
        </span>
      </div>
    </div>
  );
}
